import type { CompetenceRecord } from "../../api/client";
import { Acknowledgement } from "../../components/Acknowledgement";
import { Callout } from "../../components/Callout";
import { TextField } from "../../components/TextField";

/**
 * Screen 17 — accountability for AI-conducted audits, and the signature.
 *
 * **The signature blocks. The two acknowledgements above it do not**, and that is not
 * leniency. Assertion 6 blocks the *allocation* of an auditor whose AI accountability
 * declaration is false, and the fixtures carry a verified auditor with exactly that. A
 * record that could not be submitted with it false would put the assertion out of reach.
 * So it is recorded as given, the server answers with an advisory saying what it costs,
 * and the block lands where the fixture puts it — at allocation.
 *
 * The signature itself is the attestation held in every audit file this auditor signs. It
 * is reproduced with its original timestamp on the sign-off block of every report they
 * approve, which is why it is typed rather than inferred from the account name.
 */
export function SignStep({
  record,
  set,
  errorFor,
}: {
  record: CompetenceRecord;
  set: (patch: Partial<CompetenceRecord>) => void;
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <div className="card">
      <h2>Accountability for AI-conducted audits</h2>

      <Callout tone="block" title="Read this before signing.">
        Complao conducts the audit session and drafts the report. It does not audit on its
        own authority — it audits on yours. You remain the accountable auditor for every
        finding issued under your name.
      </Callout>

      <Acknowledgement
        id="ai_accountability"
        title="I accept the following."
        checked={record.ai_accountability ?? false}
        onChange={(ai_accountability) => set({ ai_accountability })}
      >
        I will review the audit session and the draft report, apply my own professional
        judgement, amend whatever requires amendment, and sign off before anything is
        issued. If I disagree with a finding Complao proposes, my judgement governs.
        Approving a draft without review is a failure of my professional obligation, not a
        platform behaviour.
      </Acknowledgement>

      <Acknowledgement
        id="complao_training"
        title="Platform training."
        checked={record.complao_training ?? false}
        onChange={(complao_training) => set({ complao_training })}
      >
        I have completed the Complao familiarisation module, including a full test-run
        audit, and understand how to intervene during a live session, redirect questioning,
        add findings, and pause the audit.
      </Acknowledgement>

      <div className="hr" />

      <div style={{ maxWidth: "360px" }}>
        <TextField
          id="signature"
          label="Signature"
          hint="Type your full name — this is the attestation held in every audit file you sign"
          value={record.signature ?? ""}
          onChange={(signature) => set({ signature })}
          error={errorFor("signature")}
          required
        />
      </div>
      <p className="sub">
        Recorded with a timestamp and reproduced on the sign-off block of every report you
        approve.
      </p>
    </div>
  );
}
